import axios, { AxiosHeaders } from 'axios';
import localforage from 'localforage';
import qs from 'querystring';
import {
  setupCache,
  buildStorage,
  buildKeyGenerator
} from 'axios-cache-interceptor';

const TWO_MINUTES = 2 * 60 * 1000;

const forageStore = localforage.createInstance({
  // List of drivers used
  driver: [localforage.LOCALSTORAGE],

  // Prefix all storage keys to prevent conflicts
  name: 'cwf-cache'
});

// AxiosHeaders stores its keys lowercased, so headers['Local-Cache'] reads
// undefined even when the header was set; its get() is case-insensitive.
const readHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') return headers.get(name);
  return headers[name] ?? headers[name.toLowerCase()];
};

// The cache is best effort. localforage rejects when the browser denies or
// fills local storage, and an unhandled rejection here would fail the request
// itself rather than simply miss the cache.
const bestEffort = operation => async (...args) => {
  try {
    return await operation(...args);
  } catch {
    return undefined;
  }
};

const storage = buildStorage({
  find: bestEffort(key => forageStore.getItem(key)),
  set: bestEffort((key, value) => forageStore.setItem(key, value)),
  remove: bestEffort(key => forageStore.removeItem(key)),
  // Optional in this interface; without it storage.clear is undefined and the
  // invalidation below silently does nothing.
  clear: bestEffort(() => forageStore.clear())
});

// cwf-context is part of the key because the same url returns different data
// per organization, and dropping it would serve one org's rows to another.
const generateKey = buildKeyGenerator(
  ({ url, method, params, data, headers }) =>
    `${url}:${method}:${qs.stringify(params)}:${JSON.stringify(
      data
    )}:${readHeader(headers, 'cwf-context')}`
);

const cacheOptions = {
  ttl: TWO_MINUTES,
  storage,
  generateKey,
  methods: ['get', 'post'],
  // Defaults to true, where a response's Cache-Control or Expires would
  // override the fixed TTL above, or switch caching off altogether. The
  // previous adapter read no response headers at all.
  interpretHeader: false
};

/**
 * Only requests opting in with the Local-Cache header touch the cache.
 *
 * Must run on the request, not through cachePredicate: a predicate is
 * evaluated against the response, so it blocks writes but still lets an
 * opted-out request be served an entry stored under the same key.
 *
 * Restoring AxiosHeaders is the other half. defineAxiosRequestInterceptor
 * rebuilds config.headers as a plain object, and the cache interceptor calls
 * headers.set() to attach revalidation headers. Request interceptors run
 * last-registered-first, which puts this between the two.
 */
const gateCacheOnHeader = instance => {
  instance.interceptors.request.use(config => {
    const headers = AxiosHeaders.from(config.headers);
    return readHeader(headers, 'Local-Cache')
      ? { ...config, headers }
      : { ...config, headers, cache: false };
  });

  return instance;
};

// A write can change anything, so nothing cached survives it -- except a POST
// standing in for a read.
const clearCacheOnWrite = instance => {
  instance.interceptors.response.use(async response => {
    const method = response?.config?.method?.toLowerCase();
    const isReadingByPost =
      method === 'post' &&
      readHeader(response?.config?.headers, 'Fetch-By-Post');

    if (
      (method !== 'get' && method !== 'post') ||
      (method === 'post' && !isReadingByPost)
    ) {
      // Awaited so a list reload triggered by this response cannot read back
      // the entries the write just invalidated.
      await storage.clear();
    }

    return response;
  });

  return instance;
};

export const createCachedInstance = (config = {}) =>
  clearCacheOnWrite(
    gateCacheOnHeader(setupCache(axios.create(config), cacheOptions))
  );

const instance = createCachedInstance();

const getAxiosInstance = () => instance;

const axiosConfig = {
  instances: {
    default: {
      api: getAxiosInstance()
    }
  },
  interceptors: {
    request: {
      config: {
        withCredentials: false
      }
    }
  }
};

export default axiosConfig;
